import React from "react";
import {List, StandardListCell, Button, Card, CardHeader, CardBody} from "gd-react";
import moment from "moment-timezone";
import GridDuck from "gridduck";
import "./impKwhHistory.scss";

// Formats a numeric reading, or an em-dash when there's no data in the window.
function fmt(v) {
    if (v === null || v === undefined) return '—';
    return Number(v).toLocaleString();
}


// Granularity that keeps a window to a readable number of buckets. A 48h span wants hourly;
// a year wants daily. Anything finer just makes the request slow and the line noisy.
const GRANULARITIES = [1800, 3600, 21600, 43200, 86400];
function granularityFor(spanSeconds) {
    for (const g of GRANULARITIES) {
        if (spanSeconds / g <= 200) return g;
    }
    return GRANULARITIES[GRANULARITIES.length - 1];
}

/**
 * The factor that would apply to a bucket at time `t` (epoch ms).
 *
 * Differs by correction mode, and getting this wrong is the difference between a useful
 * chart and a confident lie:
 *   rebase — every point scales by the same factor, INCLUDING the correctly-calibrated
 *            tail, because the whole series sits on one basis.
 *   first  — only the mis-recorded windows scale; everything outside them was recorded
 *            correctly and stays put.
 */
function factorAtTime(ms, periods, basis, currentImpKwh) {
    if (basis !== undefined && basis !== null && currentImpKwh && Number(basis) !== Number(currentImpKwh)) {
        return Number(basis) / Number(currentImpKwh);      // uniform rebase
    }
    for (const p of periods) {
        if (ms >= p.start && ms < p.end) return p.factor;
    }
    return 1;                                              // outside every corrected window
}

// Gas stores the RECIPROCAL of what the operator types: AssetSettingsDetails.js:68 shows
// `1 / impKwh` for DELIVERED_GAS and saves the inverse again. The correction maths is
// unaffected (our factor is recorded/current, so the inversion cancels), but showing the raw
// column here would put a different number on this tab than the Settings tab, for the same
// physical fact.
function impKwhForDisplay(value, anchorDataType) {
    if (value === null || value === undefined) return value;
    return anchorDataType === 'DELIVERED_GAS' ? 1 / Number(value) : Number(value);
}

/**
 * What Apply would actually do to this window right now.
 *
 * Deliberately NOT read off the row. The <List> is fed SDK-mapped models, and the generated
 * model only carries fields declared in bzconfig — `pending_factor` is not one of them, so
 * reading it there yields undefined and any fallback to `row.factor` silently reprints the
 * historical drift as if it were outstanding. That is the exact bug this column exists to
 * fix, so it must be derived instead.
 *
 * Mirrors pendingFactorFor() in impKwhAdjustDao. Display only — the server never trusts this.
 */
function pendingOf(row, basis) {
    if (basis === undefined || basis === null) return row.factor;   // unknown, or never corrected
    if (row.currentImpKwh === null || row.currentImpKwh === undefined) return row.factor;
    if (Number(basis) === Number(row.currentImpKwh)) return 1;
    return Number(basis) / Number(row.currentImpKwh);
}

function round4(n) {
    return Math.round(Number(n) * 10000) / 10000;
}

/**
 * A reading cell. Showing `before → after` unconditionally was actively misleading: once
 * telemetry is on the current basis nothing is outstanding, yet the table implied a large
 * pending change while the banner above said "up to date".
 */
function readingCell(row, reading, basis) {
    if (!reading || reading.before === null || reading.before === undefined) return '—';
    const p = pendingOf(row, basis);
    if (p === 1) return fmt(reading.before) + ' (no change)';
    // `after` on the row was computed by the API from ITS pending factor; recompute here for
    // the same reason pendingOf does — the SDK model does not carry it.
    return fmt(reading.before) + ' → ' + fmt(reading.before * p);
}

// ms -> value for <input type="datetime-local">
function toLocalInput(ms) {
    const d = new Date(Number(ms));
    const pad = (n) => String(n).padStart(2, '0');
    return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()) + 'T' + pad(d.getHours()) + ':' + pad(d.getMinutes());
}

/**
 * How many joins in this change log do NOT line up, read in effective-time order.
 *
 * Mirrors findChainBreak in impKwhAdjustMath.ts, but COUNTS rather than stopping at the
 * first problem, because the caller needs to compare two states rather than judge one.
 *
 * A break means the history describes a meter that never existed, and Apply would rescale
 * real telemetry by a factor derived from it. Apply refuses any break outright — that is
 * the guard that protects the data.
 *
 * Editing is judged differently, and deliberately so. Refusing every edit that leaves the
 * chain imperfect would mean that once a log IS broken, no single date change can be saved
 * unless it fixes everything at once — so a log broken in two places could never be
 * repaired through this screen at all. Letting the UI create a state it will not let you
 * leave is worse than letting you take a step that is still imperfect. So an edit is
 * allowed whenever it does not INCREASE the number of breaks.
 */
function countChainBreaks(entries, currentImpKwh) {
    const ordered = entries.slice().sort((a, b) => Number(a.effective_time) - Number(b.effective_time));
    let breaks = 0;
    for (let i = 1; i < ordered.length; i++) {
        if (Number(ordered[i - 1].new_imp_kwh) !== Number(ordered[i].old_imp_kwh)) breaks++;
    }
    const endsAt = ordered.length ? Number(ordered[ordered.length - 1].new_imp_kwh) : null;
    if (ordered.length && currentImpKwh && Number(currentImpKwh) > 0 && endsAt !== Number(currentImpKwh)) {
        breaks++;
    }
    return breaks;
}

/**
 * The first break, described so an operator can act on it. Null when the chain is sound.
 *
 * Takes anchorDataType because render()'s showImpKwh is a closure over it and not reachable
 * from here — and because gas meters display m3-per-impression, the inverse of the stored
 * value, so a message built from raw numbers would contradict every other figure on screen.
 */
function firstChainBreak(entries, currentImpKwh, anchorDataType) {
    const showImpKwh = (v) => fmt(impKwhForDisplay(v, anchorDataType));
    const ordered = entries.slice().sort((a, b) => Number(a.effective_time) - Number(b.effective_time));
    for (let i = 1; i < ordered.length; i++) {
        const previousLeftAt = Number(ordered[i - 1].new_imp_kwh);
        if (previousLeftAt !== Number(ordered[i].old_imp_kwh)) {
            return 'the ' + showImpKwh(ordered[i].old_imp_kwh) + ' → ' + showImpKwh(ordered[i].new_imp_kwh) +
                ' change would follow one that leaves the meter at ' + showImpKwh(previousLeftAt) +
                ' imp/kWh, so the history does not join up';
        }
    }
    const endsAt = ordered.length ? Number(ordered[ordered.length - 1].new_imp_kwh) : null;
    if (ordered.length && currentImpKwh && Number(currentImpKwh) > 0 && endsAt !== Number(currentImpKwh)) {
        return 'the history would end at ' + showImpKwh(endsAt) + ' imp/kWh while the meter is set to ' +
            showImpKwh(currentImpKwh);
    }
    return null;
}

function apiBase() {
    return (GridDuck.config && GridDuck.config.baseUrl) || 'http://localhost:3000/';
}

// fetch() only rejects on a NETWORK failure. A 403 or 500 resolves normally and then
// explodes inside .json(), so every load below used to land in a catch that could not tell
// "you lack the scope" from "there is nothing here" — and rendered both as an empty
// section. Check the status explicitly and carry it, so the UI can say which happened.
//
// This is not hypothetical: a stale SDK build meant the token never requested
// imp-kwh-update:view, and the change log silently rendered as "no changes logged".
function loadJson(url) {
    return fetch(url, {headers: authHeaders()}).then((r) => {
        if (!r.ok) {
            const err = new Error('HTTP ' + r.status);
            err.status = r.status;
            throw err;
        }
        return r.json();
    });
}

function authHeaders() {
    const at = GridDuck.config && GridDuck.config.accessToken;
    return {'Content-Type': 'application/json', 'Authorization': 'Bearer ' + (at ? at.token : '')};
}

// Preview + Apply of an imp_kwh history correction, with editable "effective from"
// dates per change so the correction periods align with when each imp_kwh actually
// took effect (not just when the field was edited).
class AssetImpKwhHistory extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            message: null, refreshRef: 0, logEntries: [], basis: undefined, currentImpKwh: null,
            anchorDataType: null, rateDataTypes: null, gated: false, corrections: [],
            curve: null,
            // Which loads failed, and with what status. Keyed so one failure does not
            // claim the others are empty.
            loadErrors: {},
        };
        if (props.item) {
            this.state.filters = this.buildFilters(props.item.id);
        }
        this.getList = this.getList.bind(this);
        this.apply = this.apply.bind(this);
        this.refresh = this.refresh.bind(this);
        this.fetchLog = this.fetchLog.bind(this);
        this.fetchBasis = this.fetchBasis.bind(this);
        this.fetchCorrections = this.fetchCorrections.bind(this);
        this.fetchCurve = this.fetchCurve.bind(this);
        this.rollback = this.rollback.bind(this);
        this.updateEffective = this.updateEffective.bind(this);
        this.reloadAll = this.reloadAll.bind(this);
    }

    buildFilters(assetId) {
        return [
            {field: "assetId", value: assetId},
            {field: "start", value: 0},
            {field: "end", value: Math.round(new Date().getTime() / 1000)}
        ];
    }

    getList(retrievalParams) {
        return GridDuck.getImpKwhAdjusts(retrievalParams);
    }

    // Load-failure bookkeeping. Kept as a merge rather than a flag so a 403 on the change
    // log does not erase a separate failure on the corrections list.
    withError(key, err) {
        const next = Object.assign({}, this.state.loadErrors);
        next[key] = (err && err.status) || true;
        return next;
    }

    withoutError(key) {
        const next = Object.assign({}, this.state.loadErrors);
        delete next[key];
        return next;
    }

    // Reads the raw change log (with ids + effective dates) so we can edit the dates.
    fetchLog() {
        const assetId = this.props.item.id;
        return loadJson(apiBase() + 'imp-kwh-update?asset_id=' + assetId)
            .then((d) => this.setState({logEntries: (d && d.results) || [], loadErrors: this.withoutError('log')}))
            .catch((e) => this.setState({logEntries: [], loadErrors: this.withError('log', e)}));
    }

    // Which imp_kwh basis the stored telemetry is on. Fetched raw rather than through
    // GridDuck.getImpKwhAdjusts because the generated SDK model only maps fields declared
    // in bzconfig, and would drop applied_basis.
    fetchBasis() {
        const assetId = this.props.item.id;
        const end = Math.round(new Date().getTime() / 1000);
        return loadJson(apiBase() + 'imp-kwh-adjust?asset_id=' + assetId + '&start=0&end=' + end)
            .then((d) => {
                const first = d && d.results && d.results[0];
                // Distinguish "the API says null" (never corrected) from "the API did not
                // send the field at all" (older build, or no periods). Claiming "never
                // corrected" when we simply do not know is the most alarming of the three
                // messages and the easiest to get wrong.
                const known = first && Object.prototype.hasOwnProperty.call(first, 'applied_basis');
                this.setState({
                    previewPeriods: (d && d.results ? d.results : []).map((p) => ({
                        start: p.start, end: p.end, factor: p.pending_factor,
                    })),
                    basis: known ? first.applied_basis : undefined,
                    currentImpKwh: first ? first.current_imp_kwh : null,
                    // Which series the correction covers. Undeclared in bzconfig, so the
                    // generated SDK model would drop these — they only survive because this
                    // is a direct fetch, same as applied_basis.
                    anchorDataType: first ? first.anchor_data_type : null,
                    rateDataTypes: first ? first.rate_data_types : null,
                    gated: !!(first && first.gated),
                    loadErrors: this.withoutError('basis')
                });
            })
            .catch((e) => this.setState({
                basis: undefined, currentImpKwh: null,
                anchorDataType: null, rateDataTypes: null, gated: false,
                loadErrors: this.withError('basis', e)
            }));
    }

    /**
     * The rate curve as it stands, plus what it would become if Apply ran now.
     *
     * The "proposed" series is the STORED buckets multiplied by the factor in force at each
     * one — no raw points are read. That is exact rather than an approximation: the rate
     * correction is a per-point multiply, which commutes with the averaging the aggregation
     * already did, and the register's differenced buckets are scaled by the same per-window
     * factor the delta-walk applies to each delta.
     *
     * Queried over the DATA window rather than all time: aggregation over-reports when the
     * range is much wider than the data it contains, which would inflate both series.
     */
    fetchCurve() {
        const assetId = this.props.item.id;
        const periods = this.state.previewPeriods || [];
        if (!periods.length) {
            this.setState({curve: null});
            return Promise.resolve();
        }
        const startMs = Math.min.apply(null, periods.map((p) => p.start));
        const start = Math.floor(startMs / 1000);
        const end = Math.round(new Date().getTime() / 1000);
        const gran = granularityFor(end - start);
        const type = (this.state.rateDataTypes && this.state.rateDataTypes[0]) || 'CONSUMPTION';

        // Unlike the other loads, an EMPTY curve is a legitimate everyday state — a meter
        // with no telemetry in the window has nothing to draw. So only a genuine HTTP
        // failure is reported; "no data" stays silent, or the banner would cry wolf on
        // every quiet meter.
        return loadJson(apiBase() + 'assetTelemetryBatch/' + type + '/' + start + '/' + end +
            '?data_type=' + type + '&type=average&granularity=' + gran + '&asset_id=' + assetId)
            .then((d) => {
                const res = (d && d.results && d.results[0]) || null;
                const rows = (res && res.telemetry) || [];
                const basis = this.state.basis;
                const current = this.state.currentImpKwh;
                const points = rows
                    .filter((row) => row.value !== null && row.value !== undefined)
                    .map((row) => {
                        const ms = row.time * 1000;
                        return {t: ms, before: row.value, after: row.value * factorAtTime(ms, periods, basis, current)};
                    });
                this.setState({
                    curve: points.length ? {points: points, granularity: gran, dataType: type} : null,
                    loadErrors: this.withoutError('curve')
                });
            })
            .catch((e) => this.setState({curve: null, loadErrors: this.withError('curve', e)}));
    }

    // Past corrections, newest first. `can_roll_back` is decided server-side because the
    // stack rule belongs to the endpoint that enforces it — offering a button the API would
    // refuse is worse than offering none.
    fetchCorrections() {
        return loadJson(apiBase() + 'imp-kwh-correction?asset_id=' + this.props.item.id)
            .then((d) => this.setState({corrections: (d && d.results) || [], loadErrors: this.withoutError('corrections')}))
            .catch((e) => this.setState({corrections: [], loadErrors: this.withError('corrections', e)}));
    }

    rollback(correction) {
        const points = correction.points ? correction.points.toLocaleString() : '?';
        if (!window.confirm(
            'Roll back this correction?\n\n' +
            'Restores ' + points + ' original readings across ' + correction.series + ' series.\n\n' +
            'Readings recorded AFTER this correction are not covered by its backup and will stay ' +
            'as they are, so the series may show a step where the two meet. The meter\'s imp/kWh ' +
            'setting is not changed, so this tab will show a correction as outstanding again.')) {
            return Promise.resolve();
        }
        let self = this;
        return fetch(apiBase() + 'imp-kwh-adjust/' + correction.operation_id, {
            method: 'DELETE', headers: authHeaders()
        }).then(function (r) {
            // 204, no body — reading it as JSON would throw on success.
            if (r.ok) {
                self.setState({message: 'Rollback started — restoring the original readings.'});
                // Same two-speed problem as Apply — see the comment there.
                setTimeout(function () {
                    self.setState({message: 'Rollback applied. Stored readings can take up to a minute to catch up — the graph will refresh itself.'});
                    self.reloadAll();
                }, 15000);
                setTimeout(function () {
                    self.setState({message: null});
                    self.reloadAll();
                }, 70000);
                return;
            }
            return r.text().then(function (t) {
                self.setState({message: 'Could not roll back: ' + t});
            });
        }).catch(function (e) {
            self.setState({message: 'Could not roll back: ' + (e && e.message ? e.message : e)});
        });
    }

    // Sets when a change took effect, then re-derives the preview.
    updateEffective(id, ms) {
        let self = this;

        // Judge this edit against the CURRENT state, not against perfection. Refusing
        // anything short of a sound chain would trap an already-broken log: every single
        // date change would be rejected for not fixing all of it at once. You must always
        // be able to undo a mess the same way you made it.
        const entries = this.state.logEntries || [];
        const proposed = entries.map(
            (e) => (e.id === id ? Object.assign({}, e, {effective_time: ms}) : e)
        );
        const current = this.state.currentImpKwh;
        const before = countChainBreaks(entries, current);
        const after = countChainBreaks(proposed, current);

        if (after > before) {
            // formNonce is in the input's key, so bumping it remounts the field and drops
            // the rejected value — an uncontrolled input keeps whatever was typed otherwise.
            this.setState((st) => ({
                message: 'Not saved — ' + firstChainBreak(proposed, current, this.state.anchorDataType) +
                    '. Each change has to start where the previous one ended.',
                formNonce: (st.formNonce || 0) + 1
            }));
            return Promise.resolve();
        }

        // Allowed, but say so when the log is still not sound, otherwise a partial repair
        // looks like a finished one and Apply refuses later for reasons that seem unrelated.
        const stillBroken = after > 0 ? firstChainBreak(proposed, current, this.state.anchorDataType) : null;

        return fetch(apiBase() + 'imp-kwh-update/' + id, {
            method: 'PUT', headers: authHeaders(), body: JSON.stringify({effective_time: ms})
        })
            .then(() => {
                self.setState({
                    message: stillBroken
                        ? 'Saved, but the log still does not join up — ' + stillBroken +
                          '. Apply will refuse until that is fixed too.'
                        : null
                });
                return self.reloadAll();
            })
            .catch((e) => self.setState({message: 'Error updating date: ' + e}));
    }

    // Bumping refreshRef makes the <List> re-fetch the preview with a cache-buster.
    refresh() {
        this.setState((s) => ({refreshRef: s.refreshRef + 1, filters: this.buildFilters(this.props.item.id)}));
    }

    reloadAll() {
        this.fetchLog();
        this.fetchCorrections();
        // The curve needs the periods and basis the preview returns, so it runs after it.
        this.fetchBasis().then(this.fetchCurve);
        this.refresh();
    }

    componentDidMount() {
        this.reloadAll();
    }

    componentDidUpdate(prevProps) {
        if (this.props.item && prevProps.item && this.props.item.id !== prevProps.item.id) {
            this.setState({message: null}, this.reloadAll);
        }
    }

    apply() {
        if (!window.confirm("Apply the imp_kwh correction to this meter's history?\n\nThis rewrites the stored DELIVERED/CONSUMPTION data for the affected periods. Originals are backed up first, and it is safe to re-run.")) {
            return Promise.resolve();
        }
        let self = this;
        const start = Math.round(new Date('2016-01-01').getTime() / 1000);
        const end = Math.round(new Date().getTime() / 1000) + 86400;
        // Apply is a background job: this resolves as soon as the correction has been
        // queued, NOT when it has finished. The response carries the telemetry_operation
        // id and status 0. The work itself can take a while — it rewrites every point in
        // range, and waits for a freshly written ASSET_RESET to become readable — so the
        // preview is refreshed on a delay rather than immediately, and the message says
        // "started" rather than claiming it is done.
        return GridDuck.createImpKwhAdjust({assetId: this.props.item.id, start: start, end: end})
            .then(function (res) {
                const v = (res && res.value) || (res && res[0] && res[0].value) || res || {};
                // Describe the plan the server actually chose. Counting `periods` was wrong
                // for a rebase: those windows come from the log and are never applied once
                // the telemetry sits on a single basis, so the message announced work that
                // would not happen.
                const plan = v.plan || {};
                const periods = (v.periods && v.periods.length) || 0;
                let what;
                if (plan.mode === 'skip') {
                    what = 'Nothing to apply — telemetry is already on this meter\'s setting.';
                } else if (plan.mode === 'rebase') {
                    what = 'Rebasing all readings × ' + round4(plan.factor) +
                        ' (' + fmt(plan.fromBasis) + ' → ' + fmt(plan.toBasis) + ' imp/kWh)' +
                        ' — refreshing shortly.';
                } else {
                    what = 'Correction started in the background' +
                        (periods ? ' (' + periods + ' period' + (periods === 1 ? '' : 's') + ')' : '') +
                        ' — refreshing shortly.';
                }
                self.setState({message: what});
                // TWO reloads, because two different stores settle at different speeds.
                //
                // The ledger-backed parts (basis banner, corrections list) are in Postgres and
                // are correct within a second or two. The CURVE reads stored telemetry, and
                // pythia-consumer buffers Influx writes for up to a minute — so a single
                // reload at 15s redraws the graph with pre-correction data and the correction
                // looks like it did nothing.
                setTimeout(function () {
                    self.setState({message: 'Correction applied. Stored readings can take up to a minute to catch up — the graph will refresh itself.'});
                    self.reloadAll();
                }, 15000);
                setTimeout(function () {
                    self.setState({message: null});
                    self.reloadAll();
                }, 70000);
            })
            .catch(function (e) {
                self.setState({message: 'Error: ' + (e && e.message ? e.message : e)});
                return Promise.reject(e);
            });
    }

    render() {
        const entries = this.state.logEntries || [];
        const {basis, currentImpKwh, anchorDataType, rateDataTypes, gated, corrections} = this.state;
        const showImpKwh = (v) => fmt(impKwhForDisplay(v, anchorDataType));

        // Was the last thing that happened to this meter a rollback?
        //
        // Without this the banner cannot tell "this meter drifted and needs correcting" from
        // "you just deliberately reverted this" — basis-vs-meter looks identical either way.
        // So immediately after a rollback it told the operator to apply the correction they
        // had just chosen to undo.
        const lastRollbackAt = (corrections || []).reduce(
            (a, c) => Math.max(a, c.rolled_back_at || 0), 0);
        const lastApplyAt = (corrections || []).reduce(
            (a, c) => Math.max(a, c.rolled_back ? 0 : (c.applied_at || 0)), 0);
        const justRolledBack = lastRollbackAt > 0 && lastRollbackAt > lastApplyAt;

        // A correction rescales every reading by the same factor, so the graph's shape
        // does not change and the chart re-scales its axis to fit — it can look like
        // nothing happened. This says plainly which imp_kwh the stored data is on.
        let basisNote = null;
        if (basis === undefined) {
            basisNote = null;                       // unknown — say nothing rather than guess
        } else if (basis === null) {
            basisNote = justRolledBack
                ? {text: 'You rolled back every correction — telemetry is back at the values it was originally recorded with.', tone: 'info'}
                : {text: 'Telemetry has never been corrected — still at the values it was recorded with.', tone: 'warn'};
        } else if (currentImpKwh !== null && Number(basis) === Number(currentImpKwh)) {
            basisNote = {text: 'Telemetry is on ' + showImpKwh(basis) + ' imp/kWh — up to date with this meter\'s current setting.', tone: 'ok'};
        } else if (justRolledBack) {
            // Same facts as the amber case below, but it acknowledges the operator chose
            // this rather than nagging them to redo what they just undid.
            basisNote = {
                text: 'You rolled back the last correction. Telemetry is now on ' + showImpKwh(basis) +
                    ' imp/kWh while the meter reads ' + showImpKwh(currentImpKwh) +
                    ' — apply again if you want it corrected.',
                tone: 'info'
            };
        } else {
            basisNote = {text: 'Telemetry is on ' + showImpKwh(basis) + ' imp/kWh, but the meter is set to ' + showImpKwh(currentImpKwh) + ' — apply the correction to bring it up to date.', tone: 'warn'};
        }
        // A failed load must not look like an empty result. Each of these sections renders
        // nothing when its data is absent, so without this a 403 reads as "this meter has no
        // history" — which is a confident lie about someone's data.
        const LOAD_LABELS = {
            log: 'the change log', corrections: 'past corrections',
            basis: 'the current correction state', curve: 'the before/after chart'
        };
        const failedKeys = Object.keys(this.state.loadErrors || {});
        const anyForbidden = failedKeys.some((k) => this.state.loadErrors[k] === 403);
        const loadErrorNotice = !failedKeys.length ? null : (
            <div className={'ik-notice ik-notice--warn'}>
                Could not load {failedKeys.map((k) => LOAD_LABELS[k] || k).join(', ')}.
                {' '}What you see below is incomplete — it is not a record that nothing happened.
                {anyForbidden
                    ? ' The server returned 403, which usually means your session lacks the imp-kwh scopes. Log out and back in; if it persists the API client may not be permitted them in this environment.'
                    : ' Try Refresh, and check the browser console for the failing request.'}
            </div>
        );

        const basisBanner = !basisNote ? null : (
            <div className={'ik-notice ik-notice--' + basisNote.tone}>
                {basisNote.text}
            </div>
        );
        // Which series a correction will rewrite. Worth stating plainly: it depends on the
        // meter's SKU, not on anything visible elsewhere on this page.
        const seriesNote = !anchorDataType ? null : (
            <div className={'ik-series-note'}>
                Covers <code>{anchorDataType}</code>
                {rateDataTypes && rateDataTypes.length
                    ? <span> and whichever of {rateDataTypes.map((t, i) =>
                        <span key={t}>{i > 0 ? ' / ' : ''}<code>{t}</code></span>)} this meter records</span>
                    : null}.
            </div>
        );

        const gateNote = !gated ? null : (
            <div className={'ik-notice ik-notice--warn'}>
                This meter is not electricity, so Apply is disabled. The correction is the same
                arithmetic, but it has not been verified against a real gas or water meter yet —
                enable it server-side with IMP_KWH_ENABLE_GAS_WATER.
            </div>
        );

        const changesEditor = entries.length ? (
            <div className={'ik-section'}>
                <Card>
                    <CardHeader title={'imp/kWh changes — set when each actually took effect'}/>
                    <CardBody>
                        {/* Above the rows, not below: this tells the operator what to DO, so it
                            has to be read before the inputs rather than after them. */}
                        <div className={'ik-hint ik-hint--lead'}>
                            Effective time is when the calibration really changed, not when it was
                            recorded here. It decides which readings a correction rewrites — so if a
                            meter was wrong for months before anyone noticed, backdate it.
                        </div>
                        {entries.map((e) => (
                            <div key={e.id} className={'ik-change-row'}>
                                <span className={'ik-change-values'}>
                                    {showImpKwh(e.old_imp_kwh)} → {showImpKwh(e.new_imp_kwh)} imp/kWh
                                </span>
                                <label className={'ik-change-label'}>effective from</label>
                                <input
                                    key={'eff-' + e.id + '-' + (this.state.formNonce || 0)}
                                    type="datetime-local"
                                    defaultValue={toLocalInput(e.effective_time)}
                                    onChange={(ev) => {
                                        const ms = new Date(ev.target.value).getTime();
                                        if (!isNaN(ms)) this.updateEffective(e.id, ms);
                                    }}/>
                            </div>
                        ))}
                    </CardBody>
                </Card>
            </div>
        ) : null;

        // Before / proposed, as numbers first and then a chart.
        //
        // The numbers lead deliberately: a rebase multiplies every point by the same factor,
        // so the two curves are the SAME SHAPE and only the magnitude moves. The figures say
        // that unambiguously; the chart only says it because both series share one Y scale.
        let curveBlock = null;
        const curve = this.state.curve;
        if (curve && curve.points.length > 1) {
            const pts = curve.points;
            const hours = curve.granularity / 3600;
            const avgBefore = pts.reduce((a, p) => a + p.before, 0) / pts.length;
            const avgAfter = pts.reduce((a, p) => a + p.after, 0) / pts.length;
            const kwhBefore = pts.reduce((a, p) => a + p.before * hours, 0) / 1000;
            const kwhAfter = pts.reduce((a, p) => a + p.after * hours, 0) / 1000;
            const changed = Math.abs(avgAfter - avgBefore) > 0.0001;

            const W = 720, H = 150, PAD_L = 8, PAD_R = 8, PAD_T = 18, PAD_B = 18;
            const t0 = pts[0].t, t1 = pts[pts.length - 1].t;
            // ONE scale for both series. Scaling them independently would draw a rebase as
            // two identical lines and teach the operator that Apply does nothing.
            const peak = Math.max.apply(null, pts.map((p) => Math.max(p.before, p.after))) || 1;
            const yMax = peak * 1.12;                    // headroom, so the top line is not on the edge
            const x = (t) => PAD_L + ((t - t0) / Math.max(1, t1 - t0)) * (W - PAD_L - PAD_R);
            const y = (v) => PAD_T + (1 - v / yMax) * (H - PAD_T - PAD_B);
            const path = (key) => pts.map((p, i) => (i ? 'L' : 'M') + x(p.t).toFixed(1) + ' ' + y(p[key]).toFixed(1)).join(' ');

            curveBlock = (
                <div className={'ik-section'}>
                    <Card>
                        <CardHeader title={changed ? 'What this correction would do' : 'Nothing outstanding'}/>
                        <CardBody>
                            <div className={'ik-figures'}>
                                <div className={'ik-figure'}>
                                    <div className={'ik-figure-label'}>Average rate</div>
                                    <div className={'ik-figure-value'}>
                                        {fmt(Math.round(avgBefore))} W
                                        {changed ? <span><span className={'ik-arrow'}>→</span><b>{fmt(Math.round(avgAfter))} W</b></span> : null}
                                    </div>
                                </div>
                                <div className={'ik-figure'}>
                                    <div className={'ik-figure-label'}>Total over this window</div>
                                    <div className={'ik-figure-value'}>
                                        {fmt(Math.round(kwhBefore * 10) / 10)} kWh
                                        {changed ? <span><span className={'ik-arrow'}>→</span><b>{fmt(Math.round(kwhAfter * 10) / 10)} kWh</b></span> : null}
                                    </div>
                                </div>
                                <div className={'ik-figure'}>
                                    <div className={'ik-figure-label'}>Series</div>
                                    <div className={'ik-figure-value'}>{curve.dataType}</div>
                                </div>
                            </div>
                            <svg viewBox={'0 0 ' + W + ' ' + H} className={'ik-chart'}>
                                <path d={path('before')} fill="none" className={'ik-line-before'} strokeWidth="1.5"/>
                                {changed ? <path d={path('after')} fill="none" className={'ik-line-after'} strokeWidth="1.5"/> : null}
                                <line x1={PAD_L} y1={H - PAD_B} x2={W - PAD_R} y2={H - PAD_B} className={'ik-axis'} strokeWidth="1"/>
                                <text x={PAD_L} y={H - 5} fontSize="10" className={'ik-axis-label'}>{moment(t0).format('DD/MM HH:mm')}</text>
                                <text x={W - PAD_R} y={H - 5} fontSize="10" className={'ik-axis-label'} textAnchor="end">{moment(t1).format('DD/MM HH:mm')}</text>
                                <text x={PAD_L} y={PAD_T - 6} fontSize="10" className={'ik-axis-label'}>{fmt(Math.round(peak))} W</text>
                            </svg>
                            <div className={'ik-legend'}>
                                <span><span className={'ik-swatch ik-swatch--before'}/>stored now</span>
                                {changed ? <span><span className={'ik-swatch ik-swatch--after'}/>proposed</span> : null}
                                <span className={'ik-legend-sep'}>·</span>
                                <span>both on one scale</span>
                                <span className={'ik-legend-sep'}>·</span>
                                <span>derived from the change log, so it shows the proposal rather than a measurement</span>
                            </div>
                        </CardBody>
                    </Card>
                </div>
            );
        }

        // Past corrections. Roll back is offered on the top of the stack only — undoing an
        // older one would leave the series in a state no correction ever produced, and the
        // API refuses it anyway.
        const correctionsList = !corrections || !corrections.length ? null : (
            <div className={'ik-section'}>
                <Card>
                    <CardHeader title={'Corrections applied'}/>
                    <CardBody>
                        <div className={'ik-table-scroll'}>
                        <table className={'ik-table'}>
                            <thead>
                            <tr>
                                <th>When</th>
                                <th>By</th>
                                <th>Corrected to</th>
                                <th>Readings</th>
                                <th>Status</th>
                                <th/>
                            </tr>
                            </thead>
                            <tbody>
                            {corrections.map((c) => {
                                // Status as a word AND a pill, so the column is scannable
                                // without reading every row.
                                const statusKey = c.rolled_back ? 'rolled-back' : (c.partial ? 'partial' : 'applied');
                                const statusText = c.rolled_back ? 'rolled back' : (c.partial ? 'partly applied' : 'applied');
                                return (
                                    <tr key={c.operation_id}
                                        className={c.rolled_back ? 'ik-row--rolled-back' : null}>
                                        <td className={'ik-numeric'}>{c.applied_at ? moment(c.applied_at).format('DD/MM/YY HH:mm') : '—'}</td>
                                        <td>{c.changed_by_name || '—'}</td>
                                        <td className={'ik-numeric'}>{showImpKwh(c.basis)} imp/kWh</td>
                                        <td className={'ik-numeric'}>{fmt(c.points)} over {c.series} series</td>
                                        <td>
                                            <span className={'ik-status ik-status--' + statusKey}>{statusText}</span>
                                        </td>
                                        <td className={'ik-cell-actions'}>
                                            {c.can_roll_back
                                                ? <Button outlined label={'Roll back'} onClick={() => this.rollback(c)}/>
                                                : null}
                                        </td>
                                    </tr>
                                );
                            })}
                            </tbody>
                        </table>
                        </div>
                    </CardBody>
                </Card>
            </div>
        );

        let list;
        if (this.state && this.state.filters && this.props.loaded) {
            list = (
                <List
                    nohover
                    refreshRef={this.state.refreshRef}
                    columns={[
                        {
                            component: StandardListCell, sortable: false, title: 'Period', field: 'start', maxWidth: 'none',
                            custom: (v) => moment(v.start).format('DD/MM/YY') + ' → ' + moment(v.end).format('DD/MM/YY')
                        },
                        {
                            component: StandardListCell, sortable: false, title: 'imp/kWh (was → now)', field: 'recordedImpKwh', maxWidth: 'none',
                            custom: (v) => showImpKwh(v.recordedImpKwh) + ' → ' + showImpKwh(v.currentImpKwh)
                        },
                        {
                            // How far the ORIGINAL recording was off. Kept because it
                            // explains why the window is listed at all.
                            component: StandardListCell, sortable: false, title: 'Recorded drift', field: 'factor', maxWidth: 'none',
                            custom: (v) => '× ' + v.factor
                        },
                        {
                            // What Apply would actually do to this window right now, which
                            // is a different number once a correction has already run.
                            component: StandardListCell, sortable: false, title: 'To apply', field: 'factor', maxWidth: 'none',
                            custom: (v) => pendingOf(v, basis) === 1 ? 'nothing' : '× ' + round4(pendingOf(v, basis))
                        },
                        {
                            component: StandardListCell, sortable: false, title: 'Meter reading', field: 'meterReading', maxWidth: 'none',
                            custom: (v) => readingCell(v, v.meterReading, basis)
                        },
                        {
                            component: StandardListCell, sortable: false, title: 'Rate', field: 'rateReading', maxWidth: 'none',
                            custom: (v) => readingCell(v, v.rateReading, basis)
                        }
                    ]}
                    getlist={this.getList}
                    itemsPerPage={20}
                    filters={this.state.filters}/>
            );
        }

        return (
            <div className='page imp-kwh-history'>
                <div className={'ik-actions'}>
                    <Button progressRes disabled={gated} label={'Apply correction'} onClick={this.apply}/>
                    <Button outlined label={'Refresh'} onClick={this.reloadAll}/>
                    {this.state.message ?
                        <span className={'ik-actions-message'}>{this.state.message}</span> : null}
                </div>
                {loadErrorNotice}
                {basisBanner}
                {gateNote}
                {seriesNote}
                {curveBlock}
                {correctionsList}
                {changesEditor}
                {list}
            </div>
        );
    }
}

export default AssetImpKwhHistory;
